import React from "react";
import "./styles/global.css";
import { ClerkProvider, useUser } from "@clerk/react";
import { clerkLocalization, ExternalLinkProvider } from "@scoutingops/ui";
import { Box, Button, Typography } from "@mui/material";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useNavigate } from "react-router";

import { providerProps as clerkProviderProps } from "@/lib/clerk/config";
import { Sentry } from "@/lib/sentry";
import { ThemeProvider } from "@/styles/theme";

import { Toaster } from "@/components/core/toaster";

// Centrally-managed platform links that should never show the "leaving the
// platform" interstitial — see ExternalLinkProvider's trustedUrls prop.
const trustedExternalUrls = [import.meta.env.VITE_DISCORD_URL].filter((url): url is string => Boolean(url));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30_000,
    },
  },
});

export interface RootProps {
  children: React.ReactNode;
}

export function Root({ children }: RootProps): React.JSX.Element {
  const navigate = useNavigate();

  return (
    <Sentry.ErrorBoundary fallback={<AppErrorFallback />}>
      <ClerkProvider
        telemetry={false}
        localization={clerkLocalization}
        {...clerkProviderProps}
        routerPush={(to) => {
          void navigate(to);
        }}
        routerReplace={(to) => {
          void navigate(to, { replace: true });
        }}
      >
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <SentryUserSync />
            <ExternalLinkProvider trustedUrls={trustedExternalUrls}>
              <Toaster position="bottom-right" />
              {children}
            </ExternalLinkProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </ClerkProvider>
    </Sentry.ErrorBoundary>
  );
}

// Wires the signed-in Clerk user into Sentry so errors are attributed correctly.
function SentryUserSync(): null {
  const { user } = useUser();
  React.useEffect(() => {
    if (user) {
      Sentry.setUser({
        id: user.id,
        email: user.primaryEmailAddress?.emailAddress,
        username: user.username ?? user.fullName ?? undefined,
      });
    } else {
      Sentry.setUser(null);
    }
  }, [user]);
  return null;
}

function AppErrorFallback(): React.JSX.Element {
  return (
    <Box
      sx={{
        alignItems: "center",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        justifyContent: "center",
        minHeight: "100dvh",
        p: 4,
        textAlign: "center",
      }}
    >
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        Something went wrong
      </Typography>
      <Typography color="text.secondary" sx={{ maxWidth: 400 }}>
        An unexpected error occurred. Reloading the page usually fixes it.
      </Typography>
      <Button variant="contained" onClick={() => globalThis.location.reload()}>
        Reload
      </Button>
    </Box>
  );
}

export default Root;
